import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";

/**
 * Where a staff member is in signing in:
 *   signed-out  -> password  -> needs-factor (enrol or enter the 6-digit code) -> ready
 *
 * "ready" means the session is aal2: the gateway admits /v1/admin only then.
 * Whether the account is an ADMIN is decided by the API (403 otherwise), not here.
 */
export type AuthStage = "loading" | "signed-out" | "needs-factor" | "ready";

type AuthState = { stage: AuthStage; session: Session | null; refresh: () => Promise<void>; signOut: () => Promise<void> };
const AuthContext = createContext<AuthState | null>(null);

async function stageOf(session: Session | null): Promise<AuthStage> {
  if (!session) return "signed-out";
  const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  return data?.currentLevel === "aal2" ? "ready" : "needs-factor";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ stage: AuthStage; session: Session | null }>({ stage: "loading", session: null });

  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    setState({ session: data.session, stage: await stageOf(data.session) });
  }, []);

  useEffect(() => {
    // Initial state arrives as the INITIAL_SESSION event; later sign-ins, the
    // code being verified and token refreshes arrive the same way.
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      // Not awaited inside the callback: supabase-js forbids awaiting its own
      // calls there (deadlock). Resolve the stage on the next tick instead.
      setTimeout(async () => setState({ session, stage: await stageOf(session) }), 0);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  return <AuthContext.Provider value={{ ...state, refresh, signOut }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
